/**
 * NiV "שימוש ומדדים" → top section: traffic to the PUBLIC marketing pages
 * (/, /start, /for/:slug, /blog, /login, /signup, legal) from the first-party
 * tracker (src/services/publicAnalytics.js → niv_public_pageviews).
 *
 * Shows, for the selected range (7 / 30 / 90 days):
 *  - KPI strip: views, unique visitors, leads, conversion, time on page
 *  - daily views strip
 *  - per-page table (views / uniques / time / leads / conversion / top referrer)
 *  - referrers (host + type), referrer-type and device splits, UTM sources
 *  - "what converting visitors have in common" (lead insights)
 *
 * Data: nivApi.getPublicAnalytics → Cloud Function nivPublicAnalytics.
 */

import { useMemo, useState } from "react";
import {
  Globe,
  Eye,
  UsersThree,
  UserPlus,
  Percent,
  Timer,
  Info,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { useGetPublicAnalyticsQuery } from "../../../../store/services/nivApi";
import HoverTip from "../../../../components/ui/HoverTip";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { LANDING_PAGES } from "../../../../data/landingPagesConfig";
import { DEVICE_LABELS } from "./telemetryLabels";
import "./NivPublicTrafficSection.css";

const RANGES = [
  { days: 7, label: "7 ימים" },
  { days: 30, label: "30 ימים" },
  { days: 90, label: "90 ימים" },
];

const REFERRER_TYPE_LABELS = {
  direct: "ישיר / לא ידוע",
  search: "חיפוש",
  social: "רשתות חברתיות",
  ai: "כלי AI",
  internal: "ניווט פנימי",
  other: "אתרים אחרים",
};
const REFERRER_TYPE_ORDER = ["direct", "search", "social", "ai", "other", "internal"];

const LEAD_SOURCE_LABELS = {
  get_started_page: "/start ו-/for",
  homepage_lead: "דף הבית — טופס",
  homepage_contact: "דף הבית — צור קשר",
  homepage_solutions: "דף הבית — פתרונות",
};

const SLUG_LABELS = Object.values(LANDING_PAGES).reduce((acc, p) => {
  if (p?.slug) acc[p.slug] = p.profession || p.slug;
  return acc;
}, {});

/** pageKey → Hebrew label + secondary path */
const describePage = (key) => {
  if (!key) return { label: "—", path: "" };
  if (key === "home") return { label: "דף הבית", path: "/" };
  if (key === "start") return { label: "עמוד /start", path: "/start" };
  if (key === "login") return { label: "התחברות", path: "/login" };
  if (key === "signup") return { label: "הרשמה", path: "/signup" };
  if (key === "terms") return { label: "תנאי שימוש", path: "/terms-of-service" };
  if (key === "privacy") return { label: "פרטיות", path: "/privacy-policy" };
  if (key === "accessibility") return { label: "נגישות", path: "/accessibility" };
  if (key === "blog") return { label: "בלוג", path: "/blog" };
  if (key.startsWith("blog:")) return { label: `מאמר: ${key.slice(5)}`, path: `/blog/${key.slice(5)}` };
  if (key.startsWith("for:")) {
    const slug = key.slice(4);
    if (slug === "clinic-management") return { label: "נחיתה: ניהול קליניקה", path: `/for/${slug}` };
    return { label: `נחיתה: ${SLUG_LABELS[slug] || slug}`, path: `/for/${slug}` };
  }
  return { label: key, path: "" };
};

const fmtInt = (n) => (Number(n) || 0).toLocaleString("he-IL");
const fmtPct = (n) => `${(Number(n) || 0).toLocaleString("he-IL", { maximumFractionDigits: 1 })}%`;
const fmtDuration = (ms) => {
  const s = Math.round((Number(ms) || 0) / 1000);
  if (s <= 0) return "—";
  if (s < 60) return `${s} שנ׳`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")} דק׳`;
};
const fmtDay = (dateKey) => {
  const [, m, d] = (dateKey || "").split("-");
  return m && d ? `${Number(d)}.${Number(m)}` : dateKey;
};
const pctOf = (part, total) => (total ? Math.round((part / total) * 100) : 0);

const topEntry = (obj) => {
  const entries = Object.entries(obj || {});
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return { key: entries[0][0], count: entries[0][1], pct: pctOf(entries[0][1], total) };
};

// ============================================
// Small presentational pieces
// ============================================

const Kpi = ({ icon: Icon, label, value, sub, help }) => (
  <div className="npt-kpi">
    <div className="npt-kpi-top">
      <span className="npt-kpi-icon">
        <Icon size={15} />
      </span>
      <span className="npt-kpi-label">{label}</span>
      {help && (
        <HoverTip text={help}>
          <Info size={12} className="npt-help" tabIndex={0} aria-label={help} />
        </HoverTip>
      )}
    </div>
    <span className="npt-kpi-value">{value}</span>
    {sub ? <span className="npt-kpi-sub">{sub}</span> : null}
  </div>
);

const Bars = ({ rows, total, renderMeta }) => (
  <ul className="npt-bars">
    {rows.map((r) => (
      <li key={r.key} className="npt-bar-row">
        <div className="npt-bar-head">
          <span className="npt-bar-label" title={r.key}>
            {r.label ?? r.key}
          </span>
          <span className="npt-bar-meta">{renderMeta(r)}</span>
        </div>
        <div className="npt-bar-track" aria-hidden="true">
          <i className="npt-bar-fill" style={{ width: `${total ? Math.max(2, Math.round((r.views / total) * 100)) : 0}%` }} />
        </div>
      </li>
    ))}
  </ul>
);

const SplitChips = ({ data, labels, order }) => {
  const entries = (order || Object.keys(data || {})).filter((k) => (data?.[k] || 0) > 0);
  const total = entries.reduce((s, k) => s + (data[k] || 0), 0);
  if (!entries.length) return <span className="npt-muted">—</span>;
  return (
    <div className="npt-chips">
      {entries.map((k) => (
        <span key={k} className="npt-chip">
          <b>{labels[k] || k}</b>
          <span>{pctOf(data[k], total)}%</span>
          <span className="npt-chip-n">({fmtInt(data[k])})</span>
        </span>
      ))}
    </div>
  );
};

const Fact = ({ label, value, sub }) => (
  <div className="npt-fact">
    <span className="npt-fact-label">{label}</span>
    <span className="npt-fact-value">{value}</span>
    {sub ? <span className="npt-fact-sub">{sub}</span> : null}
  </div>
);

// ============================================
// Main section
// ============================================

export default function NivPublicTrafficSection({ isAuthorized }) {
  const [days, setDays] = useState(30);
  const { data, isLoading, isFetching, isError, refetch } = useGetPublicAnalyticsQuery({ days }, { skip: !isAuthorized });

  const totals = data?.totals || {};
  const pages = data?.pages || [];
  const referrers = data?.referrers || [];
  const li = data?.leadInsights || null;
  const hasData = (totals.views || 0) > 0;

  const dailyMax = useMemo(() => Math.max(1, ...(data?.daily || []).map((d) => d.views || 0)), [data]);

  const leadFacts = useMemo(() => {
    if (!li || !li.tracked) return [];
    const facts = [];
    const topType = topEntry(li.byReferrerType);
    if (topType) facts.push({ label: "מגיעים בעיקר מ", value: REFERRER_TYPE_LABELS[topType.key] || topType.key, sub: `${topType.pct}% מהלידים` });
    if (li.topReferrers?.[0]) facts.push({ label: "אתר מפנה מוביל", value: li.topReferrers[0].key, sub: `${li.topReferrers[0].count} לידים` });
    const topDev = topEntry(li.devices);
    if (topDev) facts.push({ label: "מכשיר", value: DEVICE_LABELS[topDev.key] || topDev.key, sub: `${topDev.pct}% מהלידים` });
    if (li.medianTimeToLeadMs > 0) facts.push({ label: "זמן חציוני עד השארת פרטים", value: fmtDuration(li.medianTimeToLeadMs), sub: "מהכניסה לאתר" });
    facts.push({ label: "עמודים לפני הליד", value: (li.avgPagesBefore || 0).toLocaleString("he-IL"), sub: "בממוצע באותו ביקור" });
    if (li.topLandingPages?.[0]) facts.push({ label: "עמוד נחיתה מוביל", value: describePage(li.topLandingPages[0].key).label, sub: `${li.topLandingPages[0].count} לידים` });
    if (li.topConvertPages?.[0]) facts.push({ label: "העמוד שבו משאירים פרטים", value: describePage(li.topConvertPages[0].key).label, sub: `${li.topConvertPages[0].count} לידים` });
    const hours = (li.hours || []).map((n, h) => ({ h, n })).filter((x) => x.n > 0).sort((a, b) => b.n - a.n).slice(0, 2);
    if (hours.length) facts.push({ label: "שעות שיא", value: hours.map((x) => `${String(x.h).padStart(2, "0")}:00`).join(" · "), sub: "שעון ישראל" });
    if (Number.isFinite(li.newVisitorPct)) facts.push({ label: "מבקרים חדשים", value: `${li.newVisitorPct}%`, sub: "ביקור ראשון באתר" });
    if (li.topUtmSources?.[0]) facts.push({ label: "UTM מוביל", value: li.topUtmSources[0].key, sub: `${li.topUtmSources[0].count} לידים` });
    if (li.topProfessions?.[0]) facts.push({ label: "מקצוע מוביל", value: li.topProfessions[0].key, sub: `${li.topProfessions[0].count} לידים` });
    return facts;
  }, [li]);

  return (
    <section className="npt" aria-labelledby="npt-title">
      <header className="npt-head">
        <div className="npt-head-main">
          <span className="npt-head-icon">
            <Globe size={18} weight="fill" />
          </span>
          <div className="npt-head-text">
            <span className="npt-eyebrow">עמודים ציבוריים</span>
            <h3 id="npt-title" className="npt-title">
              מי מגיע לאתר ומאיפה
            </h3>
          </div>
        </div>
        <div className="npt-head-actions">
          <div className="npt-range" role="tablist" aria-label="טווח ימים">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                role="tab"
                aria-selected={days === r.days}
                className={`npt-range-btn${days === r.days ? " is-active" : ""}`}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" className="npt-refresh" onClick={() => refetch()} disabled={isFetching} title="רענון">
            <ArrowsClockwise size={14} className={isFetching ? "npt-spin" : ""} />
          </button>
        </div>
      </header>

      {isLoading ? (
        <ContentLoader message="טוען נתוני תנועה…" />
      ) : isError ? (
        <div className="npt-empty">לא הצלחנו לטעון את נתוני התנועה. נסו לרענן.</div>
      ) : !hasData ? (
        <div className="npt-empty">
          עדיין אין צפיות בטווח הזה. המעקב נאסף מהעמודים הציבוריים (דף הבית, /start, עמודי הנחיתה, הבלוג) מרגע הפריסה.
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="npt-kpis">
            <Kpi icon={Eye} label="צפיות" value={fmtInt(totals.views)} sub={`${fmtInt(totals.sessions)} ביקורים · ${(totals.pagesPerSession || 0).toLocaleString("he-IL")} עמודים לביקור`} />
            <Kpi
              icon={UsersThree}
              label="מבקרים ייחודיים"
              value={fmtInt(totals.uniques)}
              sub={`${fmtInt(totals.newVisitors)} חדשים`}
              help="מזהה דפדפן אנונימי (localStorage). אותו אדם בשני מכשירים נספר פעמיים."
            />
            <Kpi
              icon={UserPlus}
              label="לידים"
              value={fmtInt(totals.leads)}
              sub={totals.leadsTracked != null ? `${fmtInt(totals.leadsTracked)} עם מעקב מקור` : undefined}
              help="לידים שנוצרו מהטפסים הציבוריים בטווח. 'עם מעקב' = הביקור שהוביל לליד נרשם."
            />
            <Kpi icon={Percent} label="המרה" value={fmtPct(totals.convRate)} sub="לידים מתוך מבקרים ייחודיים" />
            <Kpi
              icon={Timer}
              label="זמן בעמוד"
              value={fmtDuration(totals.avgDurationMs)}
              sub={`חציון ${fmtDuration(totals.medianDurationMs)} · ${totals.bounceRate ?? 0}% עזבו מהר`}
              help="נמדד רק כשהעמוד גלוי על המסך. 'עזבו מהר' = ביקור של עמוד אחד מתחת ל-10 שניות."
            />
          </div>

          {/* Daily strip */}
          <div className="npt-daily" aria-label="צפיות לפי יום">
            {(data?.daily || []).map((d) => (
              <HoverTip
                key={d.dateKey}
                className="npt-daily-cell"
                text={`${fmtDay(d.dateKey)} · ${fmtInt(d.views)} צפיות · ${fmtInt(d.uniques)} ייחודיים${d.leads ? ` · ${d.leads} לידים` : ""}`}
              >
                <i
                  className={`npt-daily-bar${d.leads ? " has-lead" : ""}`}
                  style={{ height: `${Math.max(d.views ? 8 : 2, Math.round((d.views / dailyMax) * 100))}%` }}
                  tabIndex={0}
                  aria-label={`${fmtDay(d.dateKey)}: ${d.views} צפיות`}
                />
              </HoverTip>
            ))}
          </div>

          <div className="npt-grid">
            {/* Pages */}
            <div className="npt-panel">
              <h4 className="npt-panel-title">לפי עמוד</h4>
              <div className="npt-table-wrap">
                <table className="npt-table">
                  <thead>
                    <tr>
                      <th>עמוד</th>
                      <th>צפיות</th>
                      <th>ייחודיים</th>
                      <th>זמן</th>
                      <th>לידים</th>
                      <th>המרה</th>
                      <th>מפנה מוביל</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((p) => {
                      const d = describePage(p.key);
                      return (
                        <tr key={p.key}>
                          <td className="npt-td-page">
                            <span className="npt-page-label">{d.label}</span>
                            {d.path && <span className="npt-page-path">{d.path}</span>}
                          </td>
                          <td>{fmtInt(p.views)}</td>
                          <td>{fmtInt(p.uniques)}</td>
                          <td>{fmtDuration(p.avgDurationMs)}</td>
                          <td>{p.leads ? <span className="npt-badge npt-badge--lead">{fmtInt(p.leads)}</span> : <span className="npt-muted">0</span>}</td>
                          <td>{p.leads ? fmtPct(p.convRate) : <span className="npt-muted">—</span>}</td>
                          <td className="npt-td-ref" title={p.topReferrer || ""}>
                            {p.topReferrer || <span className="npt-muted">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Referrers */}
            <div className="npt-panel">
              <h4 className="npt-panel-title">מאיפה מגיעים</h4>
              {referrers.length === 0 ? (
                <div className="npt-empty npt-empty--sm">אין נתוני מפנים עדיין</div>
              ) : (
                <Bars
                  rows={referrers.slice(0, 10).map((r) => ({
                    ...r,
                    label: (
                      <>
                        {r.key === "(direct)" ? "ישיר / לא ידוע" : r.key}
                        {r.type && r.type !== "direct" && <span className={`npt-type npt-type--${r.type}`}>{REFERRER_TYPE_LABELS[r.type] || r.type}</span>}
                      </>
                    ),
                  }))}
                  total={referrers.reduce((s, r) => s + (r.views || 0), 0)}
                  renderMeta={(r) => (
                    <>
                      {fmtInt(r.views)} צפיות · {fmtInt(r.uniques)} ייחודיים
                      {r.leads ? <span className="npt-badge npt-badge--lead">{r.leads} לידים</span> : null}
                    </>
                  )}
                />
              )}
              <div className="npt-splits">
                <div className="npt-split">
                  <span className="npt-split-label">סוג מקור</span>
                  <SplitChips data={data?.referrerTypes} labels={REFERRER_TYPE_LABELS} order={REFERRER_TYPE_ORDER} />
                </div>
                <div className="npt-split">
                  <span className="npt-split-label">מכשיר</span>
                  <SplitChips data={data?.devices} labels={DEVICE_LABELS} order={["mobile", "desktop", "tablet"]} />
                </div>
                {(data?.utmSources || []).length > 0 && (
                  <div className="npt-split">
                    <span className="npt-split-label">UTM</span>
                    <div className="npt-chips">
                      {data.utmSources.slice(0, 6).map((u) => (
                        <span key={u.key} className="npt-chip" title={[u.medium, u.campaign].filter(Boolean).join(" / ")}>
                          <b>{u.key}</b>
                          <span>{fmtInt(u.views)}</span>
                          {u.leads ? <span className="npt-chip-n">· {u.leads} לידים</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lead insights */}
          <div className="npt-panel npt-panel--leads">
            <div className="npt-panel-head">
              <h4 className="npt-panel-title">מה משותף למי שמשאיר פרטים</h4>
              {li?.total ? (
                <span className="npt-panel-note">
                  {fmtInt(li.total)} לידים בטווח · {fmtInt(li.tracked)} עם נתוני ביקור
                  {li.sources && Object.keys(li.sources).length > 0 && (
                    <>
                      {" · "}
                      {Object.entries(li.sources)
                        .sort((a, b) => b[1] - a[1])
                        .map(([k, v]) => `${LEAD_SOURCE_LABELS[k] || k}: ${v}`)
                        .join(" · ")}
                    </>
                  )}
                </span>
              ) : null}
            </div>
            {leadFacts.length === 0 ? (
              <div className="npt-empty npt-empty--sm">
                {li?.total ? "הלידים בטווח נוצרו לפני תחילת המעקב — אין עדיין נתוני ביקור לקשר אליהם." : "עדיין לא נוצרו לידים מהעמודים הציבוריים בטווח הזה."}
              </div>
            ) : (
              <div className="npt-facts">
                {leadFacts.map((f) => (
                  <Fact key={f.label} {...f} />
                ))}
              </div>
            )}
          </div>

          <p className="npt-foot">
            <Info size={12} />
            מעקב עצמאי (ללא GA) · בוטים, תצוגות מקדימות ו-prerender מסוננים · מבקר ייחודי = דפדפן · נתונים גולמיים נשמרים 180 יום
            {data?.truncated ? " · הטווח גדול מדי, חלק מהצפיות לא נספרו" : ""}
          </p>
        </>
      )}
    </section>
  );
}
